import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import Layout from "@/components/feature/Layout";
import ProductCard from "@/components/base/ProductCard";
import { products } from "@/mocks/products";

const HERO_VIDEO_SRC = "https://storage.helloreaddy.io/project_files/b571cd37-6f4e-4d79-80ab-bd24e871d654/2935aff0-c76d-47d0-8042-fbaacd00bf0d_Woman_modeling_jewelry_for_video_20260917173534_.mp4";
const HERO_POSTER_SRC = "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/ccf128f323791d4cc6da912977016bf6.webp";

const promises = [
  { icon: "ri-truck-line", title: "Complimentary Shipping", copy: "Worldwide, on all orders over $200." },
  { icon: "ri-refresh-line", title: "30-Day Returns", copy: "Considered purchases, easy returns." },
  { icon: "ri-leaf-line", title: "Responsibly Made", copy: "Recycled metals, traceable stones." },
  { icon: "ri-shield-check-line", title: "Secure Checkout", copy: "Protected payments, private data." },
];

const categories = [
  { name: "Earrings", img: "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/a9f0cef27be7258c5b9dc1013ec20af5.png" },
  { name: "Necklaces", img: "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/1ae81738ccbd2ae9ef651bb517d85974.png" },
  { name: "Rings", img: "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/c755d2697ab2a4770c7cd9c103ec9f9f.png" },
  { name: "Bracelets", img: "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/ff438ae40cd938f812292092e532b876.png" },
];

const journal = [
  { title: "Worn in the first light.", tag: "Portrait", img: "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20woman%20wearing%20fine%20gold%20jewelry%20in%20soft%20natural%20window%20light%2C%20warm%20neutral%20beige%20interior%2C%20quiet%20contemporary%20jewelry%20campaign%20photography%2C%20refined%20elegant%20mood%2C%20close%20framing&width=420&height=525&seq=aurelin-journal-01&orientation=portrait" },
  { title: "The language of gold.", tag: "Journal", img: "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20woman%20wearing%20delicate%20gold%20necklace%20layered%20with%20a%20cream%20knit%2C%20soft%20natural%20daylight%2C%20warm%20neutral%20studio%20backdrop%2C%20refined%20contemporary%20jewelry%20campaign%20photography&width=420&height=525&seq=aurelin-journal-02&orientation=portrait" },
  { title: "Worn with ease.", tag: "Study", img: "https://readdy.ai/api/search-image?query=Editorial%20portrait%20of%20a%20woman%20wearing%20simple%20gold%20hoop%20earrings%20and%20a%20thin%20ring%2C%20soft%20natural%20light%2C%20warm%20neutral%20studio%20backdrop%2C%20refined%20contemporary%20jewelry%20campaign%20photography%2C%20quiet%20luxury%20aesthetic&width=420&height=525&seq=aurelin-journal-03&orientation=portrait" },
];

export default function Home() {
  const bestSellers = ["p3", "p7", "p9", "p10"].map((id) => products.find((p) => p.id === id)!).filter(Boolean);
  const heroRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const eyebrowRef = useRef<HTMLParagraphElement | null>(null);
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const ctaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hero = heroRef.current;
    const video = videoRef.current;
    if (!hero || !video) return;

    let rafId = 0;
    let currentTime = 0;
    let targetTime = 0;
    let duration = 0;
    let ready = false;

    const computeProgress = () => {
      const rect = hero.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return 0;
      const scrolled = -rect.top;
      return Math.min(1, Math.max(0, scrolled / scrollable));
    };

    const onMeta = () => {
      duration = video.duration || 0;
      ready = true;
      targetTime = computeProgress() * duration;
      currentTime = targetTime;
      try {
        if (!video.seeking) video.currentTime = currentTime;
      } catch (_e) { void _e; }
    };

    const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const fadeOut = (progress: number, start: number, end: number) => {
      if (progress <= start) return 0;
      if (progress >= end) return 1;
      return easeInOut((progress - start) / (end - start));
    };
    const applyCopy = (el: HTMLElement | null, k: number) => {
      if (!el) return;
      el.style.opacity = String(1 - k);
      el.style.transform = `translateY(${-12 * k}px)`;
      el.style.filter = k > 0.001 ? `blur(${4 * k}px)` : "";
      el.style.willChange = "opacity, transform, filter";
    };

    const tick = () => {
      const progress = computeProgress();
      if (ready && duration > 0) {
        targetTime = progress * duration;
        currentTime += (targetTime - currentTime) * 0.08;
        if (!video.seeking && Math.abs(video.currentTime - currentTime) > 0.01) {
          try {
            video.currentTime = Math.min(duration, Math.max(0, currentTime));
          } catch (_e) { void _e; }
        }
      }
      applyCopy(eyebrowRef.current, fadeOut(progress, 0.62, 0.80));
      applyCopy(headlineRef.current, fadeOut(progress, 0.65, 0.82));
      applyCopy(ctaRef.current, fadeOut(progress, 0.68, 0.84));
      rafId = requestAnimationFrame(tick);
    };

    video.pause();
    if (video.readyState >= 1 && video.duration > 0) onMeta();
    else video.addEventListener("loadedmetadata", onMeta);

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  return (
    <Layout transparentHeader>
      {/* HERO — scroll-scrub video prototype */}
      <section ref={heroRef} className="relative w-full" style={{ height: "300vh" }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden bg-stone">
          <video
            ref={videoRef}
            src={HERO_VIDEO_SRC}
            poster={HERO_POSTER_SRC}
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="relative z-10 shell h-full flex flex-col justify-end pb-14 md:pb-24 text-canvas">
            <p ref={eyebrowRef} className="eyebrow text-canvas/80 mb-3 md:mb-6 text-[10px] md:text-[12px]">Winter 2026 · Volume II</p>
            <h1 ref={headlineRef} className="font-display text-[38px] md:text-7xl leading-[1.05] max-w-3xl">
              Quiet objects,<br />considered forever.
            </h1>
            <div ref={ctaRef} className="mt-6 md:mt-10 flex flex-row gap-3 md:gap-4">
              <Link to="/shop" className="flex-1 md:flex-none text-center bg-canvas text-black px-3 py-2.5 md:px-8 md:py-3 text-[10px] md:text-[12px] tracking-[0.14em] md:tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer hover:bg-brass hover:text-canvas transition-colors">
                Shop Collection
              </Link>
              <Link to="/about" className="flex-1 md:flex-none text-center border border-canvas/60 text-canvas px-3 py-2.5 md:px-8 md:py-3 text-[10px] md:text-[12px] tracking-[0.14em] md:tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer hover:bg-canvas hover:text-black transition-colors">
                Our House
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BRAND PROMISE ROW */}
      <section className="border-b border-stone">
        <div className="shell py-8 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-5 md:gap-8">
            {promises.map((p) => (
              <div key={p.title} className="flex items-start gap-2.5 md:gap-4">
                <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-brass shrink-0">
                  <i className={`${p.icon} text-[14px] md:text-[18px]`}></i>
                </div>
                <div>
                  <p className="text-[11px] md:text-[14px] text-black leading-snug">{p.title}</p>
                  <p className="hidden md:block text-[13px] text-graphite mt-2 leading-relaxed">{p.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section>
        <div className="shell py-24">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">The Ring Edit</p>
            <h2 className="font-display text-4xl md:text-5xl text-black">A quiet study in rings</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-16" data-product-shop>
            {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="text-center mt-16">
            <Link to="/shop" className="inline-block border-b border-black text-[12px] font-medium tracking-[0.16em] uppercase pb-1 hover:text-brass hover:border-brass">
              View All
            </Link>
          </div>
        </div>
      </section>

      {/* COLLECTION CAMPAIGN */}
      <section className="bg-stone">
        <div className="relative w-full aspect-[16/7] min-h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Ultra%20sharp%20high%20resolution%20editorial%20still%20life%20photograph%20of%20recycled%2018k%20gold%20and%20platinum%20fine%20jewelry%20pieces%20arranged%20on%20soft%20warm%20beige%20linen%20fabric%2C%20delicate%20chain%20necklace%20and%20sculpted%20earrings%20catching%20soft%20morning%20window%20light%2C%20quiet%20luxury%20winter%20mood%2C%20crisp%20focus%2C%20minimal%20muted%20cream%20and%20sand%20palette%2C%20subtle%20natural%20shadows%2C%20refined%20contemporary%20jewelry%20house%20campaign%20photography&width=1600&height=700&seq=aurelin-campaign-01&orientation=landscape&nocache=true"
            alt="Aurelin Objects of Winter campaign"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent"></div>
          <div className="absolute inset-0 shell flex items-center">
            <div className="max-w-md text-canvas">
              <p className="eyebrow text-canvas/80 mb-4">Collection</p>
              <h3 className="font-display text-4xl md:text-5xl leading-tight">Objects of Winter</h3>
              <p className="mt-4 text-sm leading-relaxed text-canvas/90">
                Sculpted in recycled 18k gold and platinum. Twelve quiet pieces for the season.
              </p>
              <Link to="/shop" className="inline-block mt-8 border-b border-canvas text-[12px] tracking-[0.2em] uppercase pb-1">
                Explore the Collection
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section>
        <div className="shell py-24">
          <div className="text-center mb-12">
            <p className="eyebrow mb-4">Shop by Category</p>
            <h2 className="font-display text-4xl md:text-5xl text-black">A quiet, complete wardrobe</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {categories.map((c) => (
              <Link key={c.name} to={`/shop?category=${c.name}`} className="group cursor-pointer">
                <div className="w-full aspect-square bg-stone overflow-hidden">
                  <img src={c.img} alt={c.name} title={`${c.name} — Aurelin fine jewelry`} className="w-full h-full object-cover object-top group-hover:opacity-90 transition-opacity" />
                </div>
                <p className="mt-4 text-[15px] text-black text-center">{c.name}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIAL / CRAFT — left-bleed editorial image with soft fade */}
      <section className="relative bg-canvas overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 items-stretch gap-10 md:gap-0">
          <div className="md:col-span-7 relative">
            <div className="relative w-full aspect-[4/5] md:aspect-auto md:h-full md:min-h-[560px] overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Detailed%20studio%20photograph%20of%20jeweler%20hands%20polishing%20a%20small%20gold%20ring%20on%20a%20wooden%20workbench%2C%20warm%20natural%20light%2C%20muted%20neutral%20tones%2C%20quiet%20contemporary%20jewelry%20atelier%2C%20refined%20editorial%20photography&width=720&height=864&seq=aurelin-craft-01&orientation=portrait"
                alt="Aurelin atelier — hand finishing"
                title="Aurelin atelier — hand finishing gold"
                className="w-full h-full object-cover object-top"
              />
              <div className="hidden md:block pointer-events-none absolute inset-y-0 right-0 w-[65%] bg-gradient-to-l from-[#111111] from-0% via-[#111111]/50 via-45% to-transparent to-100%"></div>
            </div>
          </div>

          <div className="md:col-span-5 bg-[#111111] flex flex-col justify-center px-5 py-12 md:py-16 md:pl-10 lg:pl-14 md:pr-12 lg:pr-20 xl:pr-28">
            <p className="eyebrow mb-5 text-canvas/70">Material & Craft</p>
            <h3 className="font-display text-3xl md:text-4xl leading-tight text-canvas">
              Made slowly, in small runs.
            </h3>
            <div className="mt-6 max-w-[120px] h-px bg-canvas/30"></div>
            <p className="mt-8 text-canvas/75 leading-[1.9] text-[15px] max-w-lg">
              Every Aurelin piece is cast from certified recycled 18k gold or platinum, then hand-finished
              in our Antwerp atelier. We work with a single team of five, so every ring, chain and stud passes
              through the same set of hands from raw metal to final polish.
            </p>
            <Link to="/about#materials" className="inline-block mt-10 border-b border-brass text-brass text-[12px] tracking-[0.2em] uppercase pb-1 hover:brightness-110">
              Inside the Atelier
            </Link>
          </div>
        </div>
      </section>

      {/* WORN SLOWLY — Strict editorial gallery */}
      <section>
        <div className="shell py-24 md:py-28">
          <div className="flex items-end justify-between mb-10 md:mb-12 gap-6">
            <div>
              <p className="eyebrow mb-3">Journal</p>
              <h2 className="font-display text-4xl md:text-5xl text-black">Worn slowly</h2>
            </div>
            <Link to="/journal" className="hidden md:inline-block border-b border-black text-[12px] tracking-[0.2em] uppercase pb-1 hover:text-brass hover:border-brass">
              View the Journal
            </Link>
          </div>

          {/* Strict 3-equal-column editorial gallery */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {journal.map((item) => (
              <div key={item.title} className="group">
                <div className="w-full aspect-[4/5] overflow-hidden bg-stone">
                  <img
                    src={item.img}
                    alt={item.title}
                    title={`${item.title} — Aurelin journal`}
                    className="w-full h-full object-cover object-top transition-transform duration-[380ms] ease-out group-hover:scale-[1.015]"
                  />
                </div>
                <p className="mt-4 eyebrow">{item.tag}</p>
                <h4 className="mt-2 font-display text-xl md:text-[22px] text-black leading-snug">
                  <Link to="/journal" className="transition-colors hover:text-[#1E144D]">{item.title}</Link>
                </h4>
              </div>
            ))}
          </div>

          <div className="mt-10 md:hidden">
            <Link to="/journal" className="inline-block border-b border-black text-[12px] tracking-[0.2em] uppercase pb-1">
              View the Journal
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}